// common store for managing favourite collections on client and admin interfaces
import { createRESTStore } from "@stores/shared/createRESTStore";
import { createRerankAction } from "@stores/shared/createRerankAction";
import API from "@config/shared/api-paths";
import apiClient from "@services/api/apiClient";

const useFavouritesCollectionStore = createRESTStore({
  entityName: "collections",
  apiPaths: {
    fetch: "",
    create: "",
    update: "",
    delete: "",
  },
  initialState: null,

  processResponse: {
    fetch: (data) => data.items,
    create: (data) => data,
    update: (data) => data,
    delete: (data) => data,
  },

  additionalActions: (set, get) => {
    // Shared rerank action. Collections have no extra scope param; the API
    // response is merged back into the matching collection via applyResult.
    const rerankCollectionAction = createRerankAction({
      getApiPaths: () => get().apiPaths,
      applyResult: (id, data) =>
        set((state) => ({
          collections: state.collections.map((item) =>
            item.id === id ? { ...item, ...data } : item,
          ),
        })),
    });

    return {
      initializeFavouritesCollection: (interfaceType) => {
        const newApiPaths = {
          fetch:
            interfaceType === "backend"
              ? API.BASE +
                API[interfaceType.toUpperCase()].FAVOURITE_COLLECTION +
                "/tree"
              : API.BASE +
                API[interfaceType.toUpperCase()].USER_FAVOURITE_COLLECTION +
                "/tree",

          create:
            API.BASE + API[interfaceType.toUpperCase()].FAVOURITE_COLLECTION,
          update:
            API.BASE + API[interfaceType.toUpperCase()].FAVOURITE_COLLECTION,
          delete:
            API.BASE + API[interfaceType.toUpperCase()].FAVOURITE_COLLECTION,
        };
        set((state) => ({
          ...state,
          currentInterfaceType: interfaceType,
          apiPaths: newApiPaths,
          searchTerm: "",
          collections: null,
          fetching: false,
        }));
      },

      setSearchTerm: (term) => {
        set({ searchTerm: term });
      },

      addFavouriteToCollection: (collectionId, favourite) => {
        const addChild = (col) =>
          col.id === collectionId
            ? { ...col, children: [...(col.children || []), favourite] }
            : col;
        set((state) => ({ collections: state.collections.map(addChild) }));
      },

      updateFavouriteInCollection: (
        collectionId,
        favouriteId,
        updatedFavourite,
      ) => {
        const updateChildren = (children) =>
          children.map((favourite) =>
            favourite.id === favouriteId
              ? { ...favourite, ...updatedFavourite }
              : favourite,
          );
        set((state) => ({
          collections: state.collections.map((col) =>
            col.id === collectionId
              ? { ...col, children: updateChildren(col.children) }
              : col,
          ),
        }));
      },

      removeFavouriteFromCollection: (collectionId, favouriteId) => {
        const removeChild = (col) =>
          col.id === collectionId
            ? {
                ...col,
                children: col.children.filter((b) => b.id !== favouriteId),
              }
            : col;
        set((state) => ({ collections: state.collections.map(removeChild) }));
      },

      createCollection: async (data, successCallback, errorCallback) => {
        try {
          set({ creating: true });
          const result = await apiClient(get().apiPaths.create, {
            method: "POST",
            body: JSON.stringify(data),
          });

          set((state) => ({
            ...state,
            collections: [
              ...(state.collections || []),
              {
                ...result.data,
                children: [],
                type: get().currentInterfaceType === "frontend" ? 1 : 2,
              },
            ],
            creating: false,
          }));

          successCallback(result.data);
        } catch (error) {
          errorCallback(error);
          set({ creating: false });
        }
      },

      updateCollection: async (id, data, successCallback, errorCallback) => {
        try {
          const result = await apiClient(get().apiPaths.update + "/" + id, {
            method: "PUT",
            body: JSON.stringify(data),
          });

          set((state) => {
            const updatedCollection = state.collections.map((item) => {
              if (item.id === id) {
                return { ...item, ...result.data };
              }
              return item;
            });

            return {
              ...state,
              collections: updatedCollection,
              updating: false,
            };
          });

          successCallback();
        } catch (error) {
          errorCallback(error);
          set({ updating: false });
        }
      },

      deleteCollection: async (id, successCallback, errorCallback) => {
        try {
          await apiClient(`${get().apiPaths.delete}/${id}`, {
            method: "DELETE",
          });

          set((state) => ({
            ...state,
            collections: state.collections.filter((item) => item.id !== id),
          }));

          successCallback();
        } catch (error) {
          errorCallback(error);
        }
      },

      setCollectionsOrder: (newCollections) => {
        set((state) => ({ ...state, collections: newCollections }));
      },

      // Public signature preserved. URL contract + local-state merge now go
      // through the shared factory. Errors are swallowed (not rethrown) to
      // match the previous behaviour — callers fire-and-forget this action.
      rerankCollection: (id, predecessorRank, successCallback, errorCallback) =>
        rerankCollectionAction(
          id,
          predecessorRank,
          {},
          {
            onSuccess: () => {
              set({ updating: false });
              successCallback?.();
            },
            onError: (error) => {
              errorCallback?.(error);
              set({ updating: false });
            },
          },
        ).catch(() => {}),
    };
  },
});

export default useFavouritesCollectionStore;
